<section class="flex min-h-screen flex-col p-6 pb-0">
  <div class="mx-auto flex w-full max-w-[90rem] flex-col">
    <h1 class="mb-4">Manage account</h1>

    <div
      class="flex w-full flex-col border border-gray-200 bg-white pb-4"
      [ngClass]="canManageUsers() ? 'rounded-t-2xl' : 'rounded-2xl'"
    >
      <!-- Top section: Profile + Permissions -->
      <div
        class="flex w-full"
        [ngClass]="canManageUsers() ? 'divide-x divide-gray-100' : ''"
      >
        <!-- Left: Profile card -->
        <div class="p-6" [ngClass]="canManageUsers() ? 'w-1/2' : 'w-full'">
          <!-- Avatar + name -->
          <div class="flex items-center gap-4 border-b border-gray-100 pb-5">
            <div
              class="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-blue-50 font-medium text-blue-600"
            >
              {{ loggedInUser()?.Name | slice: 0 : 1
              }}{{ loggedInUser()?.Name?.split(' ')?.[1] | slice: 0 : 1 }}
            </div>
            <div>
              <h2 class="text-base font-medium text-gray-800">
                {{ loggedInUser()?.Name }}
              </h2>
              <p class="mt-0.5 text-gray-600">
                {{ loggedInUser()?.Email }}
              </p>
              <span
                class="mt-1.5 inline-flex items-center rounded-full bg-blue-50 px-2.5 py-0.5 text-sm font-medium text-blue-700"
              >
                {{ loggedInUser().Portal_Role__r?.Name }}
              </span>
            </div>
          </div>

          <!-- TODO: Uncomment after Okta Engineers enable password change permissions in Okta Stage -->
          <!-- <button
            class="rounded-full bg-gray-100 pt-2 px-2 pb-1 hover:bg-gray-50 hover:text-blue-500 transition-all"
            [matMenuTriggerFor]="accountMenu"
            aria-label="Open account menu"
          >
            <mat-icon >more_vert</mat-icon>
          </button>

          <mat-menu #accountMenu="matMenu">
            <button mat-menu-item (click)="onChangePassword()">
              <mat-icon>lock</mat-icon>
              <span>Change password</span>
            </button>
          </mat-menu> -->

          <!-- Meta grid -->
          <div class="mt-5 grid grid-cols-2 gap-4">
            <div>
              <p
                class="mb-1 text-[10px] uppercase tracking-widest text-gray-400"
              >
                Account
              </p>
              <p class="text-gray-800">
                {{ loggedInUser()?.Account?.Name || 'N/A' }}
              </p>
            </div>

            <div>
              <p
                class="mb-1 text-[10px] uppercase tracking-widest text-gray-400"
              >
                Member since
              </p>
              <p class="text-gray-800">
                {{ loggedInUser()?.CreatedDate | date: 'MMM d, y' }}
              </p>
            </div>

            <div>
              <p
                class="mb-1 text-[10px] uppercase tracking-widest text-gray-400"
              >
                Username
              </p>
              <p class="truncate text-gray-800">
                {{ loggedInUser().OktaUsername__c || 'N/A' }}
              </p>
            </div>

            <div>
              <p
                class="mb-1 text-[10px] uppercase tracking-widest text-gray-400"
              >
                Phone
              </p>
              <p class="text-gray-800">
                @if (loggedInUser()?.Phone) {
                  {{ loggedInUser().Phone | phoneNumber: 'US' }}
                } @else {
                  <span class="text-gray-400">N/A</span>
                }
              </p>
            </div>

            <div class="col-span-2">
              <p
                class="mb-1 text-[10px] uppercase tracking-widest text-gray-400"
              >
                Billing address
              </p>
              <div class="flex items-start gap-1 text-gray-800">
                <mat-icon
                  class="mt-1 !h-4 !w-4 shrink-0 !text-[16px] text-gray-400"
                  >location_on</mat-icon
                >
                @if (loggedInUser()?.Account?.BillingAddress?.street) {
                  <div>
                    <div>
                      {{ loggedInUser()?.Account?.BillingAddress?.street }}
                    </div>
                    <div>
                      {{ loggedInUser()?.Account?.BillingAddress?.city }},
                      {{ loggedInUser()?.Account?.BillingAddress?.stateCode }}
                      {{ loggedInUser()?.Account?.BillingAddress?.postalCode }}
                    </div>
                  </div>
                } @else {
                  <span class="text-gray-400">N/A</span>
                }
              </div>
            </div>
          </div>
        </div>

        <!-- Right: Roles & Permissions (read-only reference) -->
        @if (canManageUsers()) {
          <div class="w-1/2 p-6">
            <h3 class="mb-1 font-montserrat font-semibold">
              User roles &amp; permissions
            </h3>
            <p class="mb-5 text-sm text-gray-400">
              Each role grants a fixed set of permissions. Click edit next to a
              user to change a user's role.
            </p>

            <table class="w-full border-collapse text-sm">
              <thead>
                <tr>
                  <th
                    class="w-2/5 pb-3 text-left text-[10px] font-medium uppercase tracking-widest text-gray-600"
                  ></th>
                  @for (role of portalRoles$ | async; track role.Id) {
                    <th
                      class="pb-3 text-center text-[10px] font-medium uppercase tracking-widest text-gray-600"
                      [attr.id]="
                        'role-' + (role.Name.split(' ').join('-') | lowercase)
                      "
                    >
                      <div>{{ role.Name }}</div>
                    </th>
                  }
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-100">
                @for (permission of permissionList; track permission.key) {
                  <tr class="group">
                    <td
                      class="py-2 pr-4 text-sm text-gray-800 group-hover:text-gray-800"
                    >
                      {{ permission.label }}
                    </td>
                    @for (role of portalRoles$ | async; track role.Id) {
                      <td class="py-2 text-center">
                        @if (role?.Permissions__c?.includes(permission.key)) {
                          <!-- Has access -->
                          <span
                            class="mx-auto inline-flex h-[18px] w-[18px] items-center justify-center rounded-full bg-green-100"
                          >
                            <mat-icon
                              class="!h-3 !w-3 !text-[12px] text-green-600"
                              >check</mat-icon
                            >
                          </span>
                        } @else {
                          <!-- No access -->
                          <span
                            class="mx-auto inline-flex h-[18px] w-[18px] items-center justify-center"
                          >
                            <mat-icon
                              class="!h-3 !w-3 !text-[12px] text-gray-400"
                              >remove</mat-icon
                            >
                          </span>
                        }
                      </td>
                    }
                  </tr>
                }
              </tbody>
            </table>

            <!-- Legend -->
            <div
              class="mt-4 flex items-center gap-4 border-t border-gray-100 pt-4"
            >
              <div class="flex items-center gap-1.5 text-[11px] text-gray-400">
                <span
                  class="inline-flex h-[16px] w-[16px] items-center justify-center rounded-full bg-green-100"
                >
                  <mat-icon class="!h-3 !w-3 !text-[12px] text-green-600"
                    >check</mat-icon
                  >
                </span>
                Has access
              </div>
              <div class="flex items-center gap-1.5 text-[11px] text-gray-400">
                <span
                  class="inline-flex h-[16px] w-[16px] items-center justify-center"
                >
                  <mat-icon class="!h-3 !w-3 !text-[12px] text-gray-400"
                    >remove</mat-icon
                  >
                </span>
                No access
              </div>
            </div>
          </div>
        }
      </div>

      <!-- Bottom: Manage Users -->
      @if (canManageUsers()) {
        <div class="min-h-52 overflow-hidden border-t border-gray-200">
          <!-- Toolbar -->
          <div
            class="flex w-full items-center justify-between border-b border-gray-100 bg-gray-50 px-6 py-4"
          >
            <h2 class="text-lg font-medium uppercase text-gray-800">
              Manage users
              <span class="ml-1.5 font-normal lowercase text-gray-400">
                {{
                  (users$ | async).length > 0
                    ? '(' + (users$ | async).length + ' total)'
                    : ''
                }}
              </span>
            </h2>

            <div class="flex items-center gap-2">
              <!-- Search -->
              <div
                class="flex h-14 w-80 items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 text-gray-400 focus-within:border-blue-400 focus-within:ring-1 focus-within:ring-blue-400"
              >
                <mat-icon
                  class="!h-4 !w-4 shrink-0 !text-[16px] !leading-none text-gray-400"
                  >search</mat-icon
                >
                <input
                  type="text"
                  placeholder="Search users..."
                  class="w-full bg-transparent text-gray-800 placeholder-gray-400 outline-none"
                  [(ngModel)]="userSearchTerm"
                  (ngModelChange)="applyUserSearchFilter()"
                />
              </div>

              <!-- Role filter -->
              <div class="relative">
                <select
                  id="roleFilter"
                  [(ngModel)]="selectedRoleFilter"
                  (change)="applyUserSearchFilter()"
                  class="h-14 cursor-pointer appearance-none rounded-lg border border-gray-200 bg-white pl-3 pr-8 text-gray-800 focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400"
                >
                  <option value="">All roles</option>
                  @for (role of portalRoles$ | async; track role.Id) {
                    <option [value]="role.Name">{{ role.Name }}</option>
                  }
                </select>
                <mat-icon
                  class="pointer-events-none absolute right-2 top-1/2 !h-4 !w-4 -translate-y-1/2 !text-[16px] !leading-none text-gray-400"
                  >arrow_drop_down</mat-icon
                >
              </div>

              <!-- Invite -->
              <button class="btn-primary" (click)="onCreateEditUser()">
                <mat-icon class="mr-2 !h-4 !w-4 !text-[16px] !leading-none"
                  >person_add</mat-icon
                >
                Invite user
              </button>
            </div>
          </div>

          <!-- Users Table -->
          <table
            mat-table
            matSort
            [dataSource]="dataSource"
            matSortActive="FirstName"
            matSortDirection="asc"
            class="w-full"
          >
            <!-- Avatar column -->
            <ng-container matColumnDef="Number">
              <th
                mat-header-cell
                *matHeaderCellDef
                class="!w-14 !py-2.5 !pl-6 !pr-3 text-left font-montserrat text-[10px] font-semibold uppercase tracking-widest text-gray-600"
              ></th>
              <td
                mat-cell
                *matCellDef="let user"
                class="!w-14 !py-3 !pl-6 !pr-3"
              >
                <div
                  class="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-blue-100 text-sm font-medium text-blue-600"
                >
                  {{ user.FirstName | slice: 0 : 1
                  }}{{ user.LastName | slice: 0 : 1 }}
                </div>
              </td>
            </ng-container>

            <!-- User Info column -->
            <ng-container matColumnDef="UserInfo">
              <th
                mat-header-cell
                *matHeaderCellDef
                mat-sort-header
                class="!px-3 !py-2.5 text-left font-montserrat font-semibold uppercase tracking-widest"
              >
                User
              </th>
              <td mat-cell *matCellDef="let user" class="!px-3 !py-3">
                <div class="font-medium text-gray-800">
                  {{ user.LastName }}, {{ user.FirstName }}
                </div>
                <div
                  class="mt-0.5 flex items-center gap-1 text-sm text-gray-400"
                >
                  <mat-icon class="!h-3 !w-3 !text-[12px] !leading-none"
                    >mail</mat-icon
                  >
                  {{ user.Email }}
                </div>
              </td>
            </ng-container>

            <!-- Username column -->
            <ng-container matColumnDef="Username">
              <th
                mat-header-cell
                *matHeaderCellDef
                mat-sort-header
                class="!px-3 !py-2.5 text-left font-montserrat font-semibold uppercase tracking-widest"
              >
                Username
              </th>
              <td mat-cell *matCellDef="let user" class="!px-3 !py-3">
                <span class="truncate text-sm text-gray-600">{{
                  user.OktaUsername__c
                }}</span>
              </td>
            </ng-container>

            <!-- Role column -->
            <ng-container matColumnDef="Role">
              <th
                mat-header-cell
                *matHeaderCellDef
                mat-sort-header
                class="!w-24 !px-3 !py-2.5 text-left font-montserrat font-semibold uppercase tracking-widest"
              >
                Role
              </th>
              <td mat-cell *matCellDef="let user" class="!w-24 !px-3 !py-3">
                @if (user?.Portal_Role__r?.Name) {
                  <a
                    [routerLink]="[]"
                    [fragment]="
                      'role-' +
                      (user.Portal_Role__r?.Name.split(' ').join('-')
                        | lowercase)
                    "
                    class="inline-flex items-center rounded-full border border-gray-200 bg-gray-50 px-2.5 py-0.5 text-sm text-gray-800 transition-colors hover:border-gray-300"
                  >
                    {{ user.Portal_Role__r?.Name }}
                  </a>
                }
              </td>
            </ng-container>

            <!-- Created Date column -->
            <ng-container matColumnDef="CreatedDate">
              <th
                mat-header-cell
                *matHeaderCellDef
                mat-sort-header
                class="!w-36 !px-3 !py-2.5 text-left font-montserrat font-semibold uppercase tracking-widest"
              >
                Created
              </th>
              <td mat-cell *matCellDef="let user" class="!w-36 !px-3 !py-3">
                <span class="text-xs text-gray-600">{{
                  user.CreatedDate | date: 'MMM d, y'
                }}</span>
              </td>
            </ng-container>

            <!-- Last Login column -->
            <ng-container matColumnDef="LastPortalLogin">
              <th
                mat-header-cell
                *matHeaderCellDef
                mat-sort-header
                class="!w-36 !px-3 !py-2.5 text-left font-montserrat font-semibold uppercase tracking-widest"
              >
                Last login
              </th>
              <td mat-cell *matCellDef="let user" class="!w-36 !px-3 !py-3">
                <div class="flex items-center gap-1.5 text-xs">
                  @if (user?.LoggedInWithOkta__c) {
                    @if (user?.Last_Portal_Login_At__c) {
                      <span
                        class="h-1.5 w-1.5 shrink-0 rounded-full bg-green-600"
                      ></span>
                      <span class="text-gray-600">{{
                        user.Last_Portal_Login_At__c | date: 'MMM d, y'
                      }}</span>
                    } @else {
                      <span
                        class="h-1.5 w-1.5 shrink-0 rounded-full bg-gray-300"
                      ></span>
                      <span class="italic text-gray-400"
                        >History unavailable</span
                      >
                    }
                  } @else {
                    <span
                      class="h-1.5 w-1.5 shrink-0 rounded-full bg-gray-400"
                    ></span>
                    <span class="text-gray-600">Never logged in</span>
                  }
                </div>
              </td>
            </ng-container>

            <!-- Actions column -->
            <ng-container matColumnDef="Actions">
              <th
                mat-header-cell
                *matHeaderCellDef
                class="!w-24 !py-2.5 !pl-3 !pr-6 text-right font-montserrat font-semibold uppercase tracking-widest"
              ></th>
              <td
                mat-cell
                *matCellDef="let user"
                class="!w-24 !py-3 !pl-3 !pr-6"
              >
                <div class="flex items-center justify-end gap-1">
                  <button
                    (click)="onCreateEditUser(user)"
                    aria-label="Edit user"
                    matTooltip="Edit User"
                    class="flex h-7 w-7 items-center justify-center rounded-md border border-gray-200 text-gray-400 transition-colors hover:border-blue-300 hover:text-blue-600"
                  >
                    <mat-icon class="!h-4 !w-4 !text-[16px] !leading-none"
                      >edit</mat-icon
                    >
                  </button>
                  <button
                    (click)="onDeactivateUser(user)"
                    aria-label="Deactivate user"
                    matTooltip="Deactivate User"
                    class="flex h-7 w-7 items-center justify-center rounded-md border border-gray-200 text-gray-400 transition-colors hover:border-red-300 hover:text-red-500"
                  >
                    <mat-icon class="!h-4 !w-4 !text-[16px] !leading-none"
                      >delete</mat-icon
                    >
                  </button>
                  @if (!user.LoggedInWithOkta__c) {
                    <button
                      (click)="onResendInvite(user)"
                      aria-label="Resend invite"
                      matTooltip="Resend Invite"
                      class="flex h-7 w-7 items-center justify-center rounded-md border border-gray-200 text-gray-400 transition-colors hover:border-blue-300 hover:text-blue-600"
                    >
                      <mat-icon class="!h-4 !w-4 !text-[16px] !leading-none"
                        >mail</mat-icon
                      >
                    </button>
                  }
                </div>
              </td>
            </ng-container>

            <tr
              mat-header-row
              *matHeaderRowDef="displayedColumns"
              class="border-b border-gray-100 bg-white"
            ></tr>
            <tr
              mat-row
              *matRowDef="let row; columns: displayedColumns"
              class="border-b border-gray-100 transition-colors hover:bg-gray-50"
            ></tr>
          </table>

          <!-- Empty states -->
          @if ((users$ | async).length === 0) {
            <p class="py-8 text-center text-gray-400">
              No users found. Please invite a user.
            </p>
          }

          @if (
            (users$ | async).length > 0 &&
            !dataSource.filteredData.length &&
            dataSource.filter.length > 0
          ) {
            <p class="py-8 text-center text-gray-400">
              No users found
              @if (userSearchTerm && selectedRoleFilter) {
                based on your filter and search selection.
              } @else if (userSearchTerm) {
                based on your search.
              } @else if (selectedRoleFilter) {
                based on your filter selection.
              }
            </p>
          }

          <!-- Paginator -->
          <mat-paginator
            showFirstLastButtons
            [pageSize]="defaultPageSizeOption"
            [pageSizeOptions]="pageSizeOptions"
            [ngClass]="(users$ | async).length === 0 ? 'hidden' : ''"
          ></mat-paginator>
        </div>
      }
    </div>
  </div>
</section>
